import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);   // { role, full_name, organizations: { id, name, join_code, currency } }
  const [branches, setBranches] = useState([]);   // Max: all branches this user holds keys to
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(false); // arrived via a password-reset email link

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setBranches([]);
      return;
    }
    const { data } = await supabase
      .from('profiles')
      .select('*, organizations(*)')
      .eq('id', userId)
      .maybeSingle();
    setProfile(data || null);

    // Branch keychain (harmless empty result before migration 009)
    const { data: br } = await supabase.rpc('my_branches');
    setBranches(br || []);
  }, []);

  async function switchBranch(orgId) {
    const target = branches.find(b => b.id === orgId);
    showBranchSplash(target?.name || '');
    const { error } = await supabase.rpc('switch_branch', { target: orgId });
    if (error) {
      const el = document.getElementById('branch-splash');
      if (el) el.remove();
      return error;
    }
    // let the sunrise animation play a beat, then reload for the new branch
    setTimeout(() => window.location.reload(), 750);
    return null;
  }

  function showBranchSplash(name) {
    if (document.getElementById('branch-splash')) return;
    const el = document.createElement('div');
    el.id = 'branch-splash';
    el.className = 'branch-splash';
    el.innerHTML =
      '<div class="branch-splash-sun"></div>' +
      '<div class="branch-splash-name">🌅 ' +
      name.replace(/</g, '&lt;') + '</div>';
    document.body.appendChild(el);
  }

  useEffect(() => {
    let settled = false;
    const finish = () => { if (!settled) { settled = true; setLoading(false); } };

    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        setSession(session);
        await fetchProfile(session?.user?.id);
      })
      .catch(() => {})
      .finally(finish);

    // Safety net: never let the app hang on the loader (bad/slow network).
    const timer = setTimeout(finish, 7000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (event === 'PASSWORD_RECOVERY') setRecovery(true);
        setSession(newSession);
        // Refetch the profile outside the callback to avoid deadlocks
        setTimeout(() => fetchProfile(newSession?.user?.id), 0);
        finish();
      }
    );

    // When the app is re-opened from the background (common on phones),
    // re-check the session so it never sits on a stale loading state.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          setSession(session);
          fetchProfile(session?.user?.id);
        });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchProfile]);

  async function signOut() {
    await supabase.auth.signOut();
    setProfile(null);
  }

  const plan = profile?.organizations?.plan || 'basic';

  const value = {
    session,
    user: session?.user || null,
    profile,
    org: profile?.organizations || null,
    role: profile?.role || null,
    plan,
    isPro: plan === 'pro' || plan === 'max',   // Pro features (analytics, profit, carnet)
    isMax: plan === 'max',                     // Max features (AI import, per-cashier, multi-branch)
    isEvents: (profile?.organizations?.type) === 'events', // this branch is a salle/venue, not a café
    branches,
    switchBranch,
    loading,
    recovery,
    clearRecovery: () => setRecovery(false),
    refreshProfile: () => fetchProfile(session?.user?.id),
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
